import React from 'react';
import { useFavorites } from '../contexts/FavoritesContext';
import { useShop } from '../contexts/ShopContext';

/**
 * Tappable favorite toggle, positioned as a bottom-right overlay on a slide image.
 * The parent image container must be position: relative. Stops click propagation
 * so it doesn't trigger the card's open-detail handler. Mirrors the iOS
 * FavoriteButton: a star — filled delft-light (#6B9BCB) when favorited, white
 * outline otherwise, both shadowed for legibility over photos. (Star, not a heart:
 * the favorite is a utilitarian shop-internal marker, not a personal-sentiment one.)
 * Icon-agnostic name — this is the "favorite" control, independent of its glyph.
 */
interface FavoriteButtonProps {
  slideId: string;
  size?: number;
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({ slideId, size = 26 }) => {
  const { isInternalMember } = useShop();
  const { isFavorite, toggleFavorite } = useFavorites();

  // Favorites are a shop-internal working set (and slide-svc/Redis favorites deny external roles).
  // External (CLIENT/VENDOR) members never see the control.
  if (!isInternalMember) return null;

  const fav = isFavorite(slideId);

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        void toggleFavorite(slideId);
      }}
      aria-label={fav ? 'Remove from favorites' : 'Add to favorites'}
      title={fav ? 'Remove from favorites' : 'Add to favorites'}
      style={{
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        background: 'transparent',
        border: 'none',
        padding: 4,
        margin: 0,
        cursor: 'pointer',
        zIndex: 4,
        lineHeight: 0,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        /* Gold fill, but ALWAYS the white outline — matches iOS FavoriteButton. Gold alone is only
           ~2:1 against a bright photo and vanishes on a white or pale item; the white edge is what
           carries it on any background, exactly as the hollow star already does when unfavourited. */
        fill={fav ? 'var(--gold)' : 'none'}
        stroke="#FFFFFF"
        strokeWidth={2}
        strokeLinejoin="round"
        style={{ filter: 'drop-shadow(0 1px 3px rgba(0, 0, 0, 0.5))' }}
      >
        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
    </button>
  );
};

export default FavoriteButton;
